import { useState } from 'react'
import { Link } from 'react-router-dom'

import { IdentityChip } from '../components/Avatar'
import { EmptyState, RarityChip, SectionHeading, Skeleton } from '../components/ui'
import { useLeaderboard } from '../hooks/queries'
import { cents, multiple, timeAgo } from '../lib/format'

type Window = 'day' | 'week' | 'all'

const WINDOWS: Array<[Window, string]> = [
  ['day', '24 hours'],
  ['week', '7 days'],
  ['all', 'All time'],
]

export function LeaderboardPage() {
  const [window, setWindow] = useState<Window>('week')
  const { data: entries, isLoading } = useLeaderboard(window)

  return (
    <div className="py-6">
      <SectionHeading
        title="Leaderboard"
        sub="Ranked by return multiple, not by dollars — otherwise it is only a list of who staked the most."
        action={
          <div className="flex gap-1">
            {WINDOWS.map(([value, label]) => (
              <button
                key={value}
                onClick={() => setWindow(value)}
                className="rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors"
                style={{
                  background: window === value ? 'var(--color-brand)' : 'var(--color-surface-2)',
                  borderColor: window === value ? 'var(--color-brand)' : 'var(--color-line)',
                  color: window === value ? '#101010' : 'var(--color-muted)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : !entries || entries.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          body="No packs have been opened in this window. Open one and you will be the whole board."
          action={
            <Link to="/packs" className="btn btn-primary btn-sm">
              Browse packs
            </Link>
          }
        />
      ) : (
        <div className="panel overflow-hidden">
          <div className="hidden border-b px-5 py-2.5 text-[0.6875rem] font-semibold tracking-wider uppercase sm:grid sm:grid-cols-[2.5rem_1fr_10rem_7rem_7rem]"
            style={{ color: 'var(--color-faint)' }}
          >
            <span>#</span>
            <span>Player</span>
            <span>Pack</span>
            <span className="text-right">Returned</span>
            <span className="text-right">Multiple</span>
          </div>

          <div className="divide-y">
            {entries.map((entry) => (
              <div
                key={entry.openId}
                className="grid grid-cols-[2.5rem_1fr_7rem] items-center gap-2 px-5 py-3 sm:grid-cols-[2.5rem_1fr_10rem_7rem_7rem]"
              >
                <span
                  className="tnum text-sm font-bold"
                  style={{
                    color:
                      entry.rank === 1
                        ? 'var(--color-brand)'
                        : entry.rank <= 3
                          ? 'var(--color-text)'
                          : 'var(--color-faint)',
                  }}
                >
                  {entry.rank}
                </span>

                <div className="min-w-0">
                  <IdentityChip identity={entry.by} size={22} bold className="text-sm" />
                  <div
                    className="truncate text-[0.6875rem] sm:hidden"
                    style={{ color: 'var(--color-faint)' }}
                  >
                    {entry.packName} · {cents(entry.totalCents)}
                  </div>
                  <div className="mt-1 hidden items-center gap-2 sm:flex">
                    <RarityChip rarity={entry.best} />
                    {entry.topPull && (
                      <span className="text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                        {entry.topPull.symbol} · {cents(entry.topPull.valueCents)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="hidden min-w-0 sm:block">
                  <Link
                    to={`/pack/${entry.packId}`}
                    className="block truncate text-xs hover:underline"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {entry.packName}
                  </Link>
                  <div className="tnum text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                    {cents(entry.priceCents)} in · {timeAgo(entry.openedAt)}
                  </div>
                </div>

                <div className="tnum hidden text-right text-sm font-semibold sm:block">
                  {cents(entry.totalCents)}
                </div>

                <div className="text-right">
                  <span
                    className="tnum text-sm font-bold"
                    style={{ color: entry.multiple >= 1 ? 'var(--color-up)' : 'var(--color-down)' }}
                  >
                    {multiple(entry.multiple)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
